import { useMemo, useState } from 'react';
import { Typography, Stack, Button, CardActions } from '@mui/material';
import { Field, Formik, FormikProps } from 'formik';
import { TextField } from 'formik-mui';
import { LegendItem, LegendList } from 'shared/ui/legend-list';
import { CardPaper } from 'shared/ui/card-paper';
import { DatePickerField } from 'shared/ui/formik-fields/date-picker';
import { useAdvanceInvoice } from 'entities/invoice/invoice.hook';
import { useSnack } from 'shared/ui/snack';
import { CreateAdvanceInvoiceDto } from '@sr/dto';
import { invoiceDataApi } from 'entities/invoice/details/api';

type FormData = {
  documentNumber: string;
  createdAt: Date | null;
};

type Props = {
  invoiceId: number;
};

export const AdvanceInvoice = ({ invoiceId }: Props) => {
  const { advanceInvoice } = useAdvanceInvoice(invoiceId);
  const { showSuccess, showError } = useSnack();

  const [isEditing, setIsEditing] = useState(false);

  const [addAdvanceInvoice, { isLoading: isCreating }] = invoiceDataApi.useAddAdvanceInvoiceMutation();
  const [updateAdvanceInvoice, { isLoading: isUpdating }] = invoiceDataApi.useUpdateAdvanceInvoiceMutation();
  const [deleteAdvanceInvoice, { isLoading: isDeleting }] = invoiceDataApi.useDeleteAdvanceInvoiceMutation();

  const isSubmitting = isCreating || isUpdating;

  const initialValues = useMemo<FormData>(() => ({
    documentNumber: advanceInvoice?.documentNumber ?? '',
    createdAt: advanceInvoice?.createdAt ? new Date(advanceInvoice.createdAt) : new Date(),
  }), [advanceInvoice]);

  const handleSubmit = async (values: FormData) => {
    const dto: CreateAdvanceInvoiceDto = {
      documentNumber: values.documentNumber,
      createdAt: values.createdAt!,
      invoiceId
    };
    try {
      if (advanceInvoice) {
        await updateAdvanceInvoice({ invoiceId, id: advanceInvoice.id, dto }).unwrap();
        showSuccess('Авансовая счёт-фактура обновлена');
      } else {
        await addAdvanceInvoice({ invoiceId, dto }).unwrap();
        showSuccess('Авансовая счёт-фактура создана');
      }
      setIsEditing(false);
    } catch (e) {
      showError('Ошибка сохранения авансовой счёт-фактуры', e);
    }
  };

  const handleDelete = async () => {
    if (!advanceInvoice) return;
    if (!window.confirm('Удалить авансовую счёт-фактуру?')) return;
    try {
      await deleteAdvanceInvoice({ invoiceId, id: advanceInvoice.id }).unwrap();
      showSuccess('Авансовая счёт-фактура удалена');
      setIsEditing(false);
    } catch (e) {
      showError('Ошибка удаления авансовой счёт-фактуры', e);
    }
  };

  const validate = (values: FormData) => {
    const errors: Partial<Record<keyof FormData, string>> = {};
    if (!values.documentNumber?.trim()) errors.documentNumber = 'Обязательное поле';
    if (!values.createdAt) errors.createdAt = 'Обязательное поле';
    return errors;
  };

  const showEditForm = isEditing || !advanceInvoice;

  return (
    <CardPaper sx={{ minWidth: 400 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Авансовая счёт-фактура</Typography>

      {showEditForm ? (
        <Formik initialValues={initialValues} validate={validate} onSubmit={handleSubmit}>
          {({ submitForm, dirty, isValid }: FormikProps<FormData>) => (
            <>
              <Stack direction="column" spacing={2}>
                <Field
                  name="documentNumber"
                  label="Номер документа"
                  component={TextField}
                  required
                />
                <Field
                  name="createdAt"
                  label="Дата создания"
                  component={DatePickerField}
                />
              </Stack>

              <CardActions sx={{ px: 0, mt: 2 }}>
                {advanceInvoice && (
                  <Button onClick={() => setIsEditing(false)} disabled={isSubmitting || isDeleting}>
                    Отмена
                  </Button>
                )}
                <Button
                  sx={{ ml: 'auto' }}
                  variant="contained"
                  onClick={submitForm}
                  disabled={!dirty || isSubmitting || isDeleting || !isValid}
                >
                  Сохранить
                </Button>
              </CardActions>
            </>
          )}
        </Formik>
      ) : (
        <>
          <LegendList>
            <LegendItem title="Номер документа:" value={advanceInvoice!.documentNumber} />
            <LegendItem
              title="Дата создания:"
              value={new Date(advanceInvoice!.createdAt).toLocaleDateString('ru-RU')}
            />
          </LegendList>

          <CardActions sx={{ px: 0, mt: 2 }}>
            <Button
              color="error"
              onClick={handleDelete}
              disabled={isDeleting || isSubmitting}
            >
              Удалить
            </Button>
            <Button
              sx={{ ml: 'auto' }}
              variant="outlined"
              onClick={() => setIsEditing(true)}
              disabled={isDeleting}
            >
              Редактировать
            </Button>
          </CardActions>
        </>
      )}
    </CardPaper>
  );
};